/**
 * Laptop scans as coverage (release 2.1 item 17).
 *
 * "12,850 of 13,000 laptops scanned in the last 24 h", one bar that adds up
 * to every enrolled laptop, and a paged list of the EXCEPTIONS only: failed,
 * skipped files, paused, checking in without scanning, silent, down, never
 * checked in. Laptop runs never create a row in the scan list below this card
 * (the agent reports its last run on its check-in), so this card is where
 * they are counted.
 *
 * Release 3.1: the words are the one fleet vocabulary (COVERAGE_LABEL in
 * fleet-format.ts); the API's state values, `stale` included, are unchanged.
 *
 * Coverage is read live from the laptop rows (no rollup lag).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Laptop, Loader2, RefreshCw } from 'lucide-react';
import { ErrorState, LoadingState } from '@foctta/ui-shell';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import {
  getFleetCoverage,
  getFleetExceptions,
  type CoverageException,
  type CoverageState,
  type FleetCoverage,
} from '@/lib/fleet-api';

import {
  COVERAGE_BAR_CLASS,
  COVERAGE_LABEL,
  COVERAGE_ORDER,
  ago,
  barSegments,
  fmtCount,
  plural,
} from './fleet-format';
import { useLatestGuard } from './use-latest';

const WINDOWS: Array<{ hours: number; label: string }> = [
  { hours: 24, label: 'Last 24 h' },
  { hours: 72, label: 'Last 3 days' },
  { hours: 168, label: 'Last 7 days' },
];

const EXCEPTION_TABS: CoverageState[] = [
  'failed',
  'scanned_with_skips',
  'paused',
  'not_attempted',
  'silent',
  'stale',
  'never_reported',
];

export function CoverageBar({ coverage, onPick }: { coverage: FleetCoverage; onPick?: (s: CoverageState) => void }) {
  const segs = barSegments(coverage.states, COVERAGE_ORDER);
  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted" role="img" aria-label="Laptop scan coverage">
        {segs
          .filter((s) => s.count > 0)
          .map((s) => (
            <div
              key={s.key}
              className={cn('h-full', COVERAGE_BAR_CLASS[s.key])}
              style={{ width: `${s.pct}%` }}
              title={`${COVERAGE_LABEL[s.key]}: ${fmtCount(s.count)}`}
            />
          ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {segs.map((s) => (
          <button
            key={s.key}
            type="button"
            disabled={!onPick || s.key === 'scanned'}
            onClick={() => onPick?.(s.key)}
            className="flex items-center gap-1.5 text-[11px] text-muted-foreground enabled:hover:text-foreground"
          >
            <span className={cn('h-2 w-2 rounded-full', COVERAGE_BAR_CLASS[s.key])} />
            {COVERAGE_LABEL[s.key]} <span className="font-medium text-foreground">{fmtCount(s.count)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ExceptionRow({ row }: { row: CoverageException }) {
  return (
    <tr className="border-t border-border/30">
      <td className="py-2 pr-3">
        <div className="text-xs font-medium">{row.label}</div>
        {row.hostname && row.hostname !== row.label && (
          <div className="text-[11px] text-muted-foreground">{row.hostname}</div>
        )}
      </td>
      <td className="py-2 pr-3 text-xs text-muted-foreground">
        {row.businessUnitId === null ? 'Unassigned' : row.businessUnitName ?? 'Not visible to you'}
      </td>
      <td className="py-2 pr-3 text-xs">{row.reason}</td>
      <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap">{ago(row.lastHeartbeatAt)}</td>
      <td className="py-2 text-xs text-muted-foreground whitespace-nowrap">
        {row.lastScanFinishedAt ? `${ago(row.lastScanFinishedAt)}${row.lastScanOutcome ? ` (${row.lastScanOutcome.replace(/_/g, ' ')})` : ''}` : 'never'}
      </td>
    </tr>
  );
}

export function LaptopCoverageCard({
  bu,
  onCoverage,
  onLoadFailed,
}: {
  bu?: string;
  /** Told each headline as it loads, for a page that shows the card on a tab. */
  onCoverage?: (coverage: FleetCoverage) => void;
  /** Told whether the headline could not be loaded, for the same reason. */
  onLoadFailed?: (failed: boolean) => void;
} = {}) {
  const [windowHours, setWindowHours] = useState(24);
  const [coverage, setCoverage] = useState<FleetCoverage | null>(null);
  const [covError, setCovError] = useState<string | null>(null);
  const [tab, setTab] = useState<CoverageState>('failed');
  const [rows, setRows] = useState<CoverageException[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Two guards: the headline and the list reload independently. A late
  // answer for an earlier window, business unit or tab must not overwrite
  // (or be appended to) the current one.
  const beginCoverage = useLatestGuard();
  const beginPage = useLatestGuard();

  const loadCoverage = useCallback(async () => {
    const current = beginCoverage();
    setCovError(null);
    try {
      const c = await getFleetCoverage({ windowHours, ...(bu ? { bu } : {}) });
      if (!current()) return;
      setCoverage(c);
      onCoverage?.(c);
      onLoadFailed?.(false);
      // Open the first exception tab that has laptops in it.
      const first = EXCEPTION_TABS.find((s) => (c.states[s] ?? 0) > 0);
      if (first) setTab((prev) => ((c.states[prev] ?? 0) > 0 ? prev : first));
    } catch (e) {
      if (!current()) return;
      setCovError(e instanceof Error ? e.message : 'Could not load laptop coverage');
      onLoadFailed?.(true);
    }
  }, [windowHours, bu, beginCoverage, onCoverage, onLoadFailed]);

  const loadPage = useCallback(
    async (state: CoverageState, after: string | null) => {
      const current = beginPage();
      setListLoading(true);
      setListError(null);
      try {
        const page = await getFleetExceptions({
          windowHours,
          state,
          limit: 50,
          ...(after ? { cursor: after } : {}),
          ...(bu ? { bu } : {}),
        });
        if (!current()) return;
        setRows((prev) => (after ? [...prev, ...page.items] : page.items));
        setCursor(page.nextCursor);
        setTotal(page.total);
      } catch (e) {
        if (!current()) return;
        setListError(e instanceof Error ? e.message : 'Could not load the laptop list');
      } finally {
        if (current()) setListLoading(false);
      }
    },
    [windowHours, bu, beginPage],
  );

  useEffect(() => {
    void loadCoverage();
  }, [loadCoverage]);

  useEffect(() => {
    setRows([]);
    setCursor(null);
    void loadPage(tab, null);
  }, [tab, loadPage]);

  const windowLabel = WINDOWS.find((w) => w.hours === windowHours)?.label.toLowerCase() ?? `last ${windowHours} h`;

  return (
    <GlassCard className="p-5 mb-6">
      <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Laptop className="h-4 w-4 text-primary" /> Laptop scan coverage
          </CardTitle>
          <CardDescription clamp className="text-[11px]">
            Every enrolled laptop is counted once. Laptop runs are reported by the agent on its check-in, so they
            are counted here and not in the run list.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select
            aria-label="Time window"
            value={windowHours}
            onChange={(e) => setWindowHours(Number(e.target.value))}
            className="glass-lite text-xs rounded px-2 py-1 bg-background/40 border border-border/40"
          >
            {WINDOWS.map((w) => (
              <option key={w.hours} value={w.hours}>
                {w.label}
              </option>
            ))}
          </select>
          <Button size="sm" variant="glass" onClick={() => { void loadCoverage(); void loadPage(tab, null); }}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </CardHeader>

      {covError && <ErrorState title="Could not load laptop coverage" detail={covError} onRetry={() => void loadCoverage()} />}
      {!covError && !coverage && <LoadingState caption="Counting laptops..." />}

      {coverage && coverage.laptops === 0 && (
        <div className="glass-lite rounded-md p-6 text-center text-sm text-muted-foreground">
          <Laptop className="h-7 w-7 mx-auto mb-2 opacity-50" />
          <div className="font-medium text-foreground">No laptops have enrolled yet</div>
          <div className="mt-1 text-xs">
            Install the endpoint agent from{' '}
            <Link to="/admin/edge-provisioning" className="text-primary hover:underline">Edge provisioning</Link>; each laptop
            appears here after its first check-in.
          </div>
        </div>
      )}

      {coverage && coverage.laptops > 0 && (
        <>
          <div className="mb-3">
            <div className="text-2xl font-semibold tabular-nums">
              {fmtCount(coverage.scanned)} <span className="text-base font-normal text-muted-foreground">of</span>{' '}
              {plural(coverage.laptops, 'laptop')}
            </div>
            <div className="text-xs text-muted-foreground">
              scanned in the {windowLabel}
              {coverage.coveragePct !== null ? ` (${coverage.coveragePct}%)` : ''}
              {coverage.scannedWithSkips > 0 ? `; ${fmtCount(coverage.scannedWithSkips)} of those did not read some files` : ''}.
              Checked {ago(coverage.asOf)}.
            </div>
          </div>
          <CoverageBar coverage={coverage} onPick={(s) => setTab(s)} />

          <div className="mt-5">
            <div className="flex flex-wrap gap-1 mb-2" role="tablist" aria-label="Laptops needing attention">
              {EXCEPTION_TABS.map((s) => (
                <button
                  key={s}
                  type="button"
                  role="tab"
                  aria-selected={tab === s}
                  onClick={() => setTab(s)}
                  className={cn(
                    'rounded px-2.5 py-1 text-[11px] transition-colors',
                    tab === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50',
                  )}
                >
                  {COVERAGE_LABEL[s]} ({fmtCount(coverage.states[s] ?? 0)})
                </button>
              ))}
            </div>

            {listError && <ErrorState title="Could not load the laptop list" detail={listError} onRetry={() => void loadPage(tab, null)} />}
            {!listError && listLoading && rows.length === 0 && <LoadingState caption="Loading laptops..." />}
            {!listError && !listLoading && rows.length === 0 && (
              <div className="glass-lite rounded-md p-4 text-center text-xs text-muted-foreground">
                No laptops in this group for the {windowLabel}.
              </div>
            )}
            {rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="pb-1 pr-3 font-medium">Laptop</th>
                      <th className="pb-1 pr-3 font-medium">Business unit</th>
                      <th className="pb-1 pr-3 font-medium">Why it is listed</th>
                      <th className="pb-1 pr-3 font-medium">Last check-in</th>
                      <th className="pb-1 font-medium">Last scan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <ExceptionRow key={r.podRowId} row={r} />
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>
                    Showing {fmtCount(rows.length)} of {fmtCount(total)}
                  </span>
                  {cursor && (
                    <Button size="sm" variant="glass" disabled={listLoading} onClick={() => void loadPage(tab, cursor)}>
                      {listLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                      Load 50 more
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </GlassCard>
  );
}
